import { useEffect, useState } from "react";
import { Form, Typography, Image, Popconfirm } from 'antd';
import { Table } from "antd";
import { useDetailModel } from "../detailModel";
import { uploadDataModel } from "./uploadDataModel";
import { InvoiceIdentify } from "@/views/invoice/manage/InvoiceManageTypes";
import { invoiceManageService } from "@/views/invoice/manage/InvoiceManageService";
import { invoiceModel } from "@/views/invoice/manage/InvoiceModel";
import { EditableCell } from "@/components/Custom/EditTableRow";
import moment from 'moment';

export function UploadDataTable() {

    const { imageUrl } = invoiceModel();
    const { dataSource, loadData, pgCfg, onSearchInvoiceNums, setDetailVisible, setDetail } = uploadDataModel();
    const { customer } = useDetailModel();

    const [form] = Form.useForm();
    const [editingKey, setEditingKey] = useState<string>('');
    const isEditing = (record: InvoiceIdentify) => record.id === editingKey;

    const edit = (record: InvoiceIdentify) => {
        form.setFieldsValue({
            ...record,
            invoiceDate: moment(record.invoiceDate)
        });
        setEditingKey(record.id ?? "");
    };

    const save = async (key: React.Key) => {
        try {
            const row = (await form.validateFields()) as InvoiceIdentify;
            const newDataSource = [...dataSource];
            const index = newDataSource.findIndex((item) => key === item.id);
            if (index > -1) {
                const item = newDataSource[index];
                newDataSource.splice(index, 1, {
                    ...item,
                    ...row,
                    invoiceDate: moment(row.invoiceDate).format('YYYY-MM-DD')
                });
                setEditingKey('');
                let ret = await invoiceManageService.invoiceUploadUpdate(newDataSource[index]);
                if (ret) {
                    onSearchInvoiceNums()
                }
            }
        }
        catch (errInfo) {
            console.log('数据保存异常:', errInfo);
        }
    };

    const deleteData = async (id: string) => {
        try {
            let ret = await invoiceManageService.invoiceUploadDelete(id);
            if (ret) {
                onSearchInvoiceNums()
            }
        }
        catch (errInfo) {
            console.log('数据删除异常:', errInfo);
        }
    };

    const openDetail = (record: InvoiceIdentify) => {
        setDetail(record);
        setDetailVisible(true);
    };

    const columns = [
        {
            title: '序号',
            dataIndex: '',
            width: 40,
            align: 'center' as const,
            render: (v: any, r: any, i: number) => i + 1
        },
        {
            title: '发票图片',
            dataIndex: 'invoiceImage',
            width: 100,
            align: 'center' as const,
            render: (v: any, r: InvoiceIdentify, i: number) => {
                return (
                    <Image style={{ maxHeight: '40px' }} src={imageUrl(r.md5)} />
                )
            },
        },
        {
            title: '客户名称',
            dataIndex: 'purchaserName',
            width: 150,
            align: 'center' as const,
            editable: true,
            inputType: 'text',
            render: (v: any, r: any, i: number) => {
                return (
                    customer.name?.trim() != v.trim() ? <div style={{ color: 'red' }}>{v}</div> : <div>{v}</div>
                )
            }
        },
        {
            title: '销售方名称',
            dataIndex: 'sellerName',
            width: 150,
            align: 'center' as const,
            editable: true,
            inputType: 'text',
        },
        {
            title: '发票号码',
            dataIndex: 'invoiceNum',
            width: 100,
            align: 'center' as const,
            editable: true,
            inputType: 'text',
        },
        {
            title: '开票名称',
            dataIndex: 'invoiceTypeName',
            width: 80,
            align: 'center' as const,
            editable: true,
            inputType: 'text',
        },
        {
            title: '开票日期',
            dataIndex: 'invoiceDate',
            width: 100,
            align: 'center' as const,
            editable: true,
            inputType: 'date',
        },
        {
            title: '发票类型',
            dataIndex: 'invoiceTypeCode',
            width: 100,
            align: 'center' as const,
            editable: true,
            inputType: 'select',
            render: (v: any, r: any, i: number) =>
                v == 'invoice-type.no' ? '无票' :
                    v == 'invoice-type.vat-ordinary' ? '增值税普通发票' :
                        v == 'invoice-type.vat-special' ? '增值税专用发票' : '',
            valueEnum: [
                { label: "无票", value: "invoice-type.no" },
                { label: "增值税专用发票", value: "invoice-type.vat-special" },
                { label: "增值税普通发票", value: "invoice-type.vat-ordinary" },
            ]
            ,
        },
        {
            title: '开票金额',
            dataIndex: 'amount',
            width: 100,
            align: 'center' as const,
            editable: true,
            inputType: 'number',
        },
        {
            title: '纳税人识别号',
            dataIndex: 'purchaserRegisterNum',
            width: 100,
            align: 'center' as const,
            editable: true,
            inputType: 'text',
        },
        {
            title: '地址、电话',
            dataIndex: 'purchaserAddress',
            width: 200,
            align: 'center' as const,
            editable: true,
            inputType: 'text',
        },
        {
            title: '开户行及账号',
            dataIndex: 'purchaserBank',
            width: 200,
            align: 'center' as const,
            editable: true,
            inputType: 'text',
        },
        {
            title: '操作',
            dataIndex: 'operation',
            width: 120,
            align: 'center' as const,
            render: (v: any, r: InvoiceIdentify, i: number) => {
                const editable = isEditing(r);
                return editable ? (
                    <span>
                        <Typography.Link style={{ marginRight: 8 }} onClick={() => save(r.id)}>
                            保存
                        </Typography.Link>
                        <Typography.Link style={{ marginRight: 8 }} onClick={() => setEditingKey('')}>
                            取消
                        </Typography.Link>
                    </span>
                ) : (
                    <span>
                        <Typography.Link style={{ marginRight: 8 }} disabled={editingKey !== ''} onClick={() => edit(r)}>
                            编辑
                        </Typography.Link>
                        <Popconfirm title="确认删除吗?" onConfirm={() => deleteData(r.id)}>
                            <a>删除</a>
                        </Popconfirm>
                        <Typography.Link style={{ marginLeft: 8 }} onClick={() => openDetail(r)}>
                            详情
                        </Typography.Link>
                    </span>
                );
            },
        },
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record: InvoiceIdentify) => ({
                record,
                inputType: col.inputType,
                dataIndex: col.dataIndex,
                title: col.title,
                valueEnum: col.valueEnum,
                editing: isEditing(record),
            }),
        };
    });

    useEffect(() => {
        onSearchInvoiceNums();
    }, [])

    return (
        <Form form={form} component={false}>
            <Table
                rowKey={record => record.id}
                components={{
                    body: {
                        cell: EditableCell,
                    },
                }}
                columns={mergedColumns}
                dataSource={dataSource}
                pagination={{
                    ...pgCfg,
                    showTotal: (total: number) => `共${total}条`,
                    position: ["bottomCenter"],
                    onChange: (pageNum, pageSize) => {
                        loadData({ pageNum, pageSize })
                    }
                }}
                scroll={{ x: 'max-content', y: "calc(100vh - 446px)" }}
            />
        </Form>
    )
}